// apps/share-web/src/chart/chart-song.ts
//
// Что можно сказать о песне, НЕ зная, откуда она приехала.
//
// Источников два, и они разной формы: манифест аудио-шейра (одна песня со
// своими дорожками и пиками) и нагрузка текстовой ссылки на сетлист
// (`TextSharePayload` — весь сетлист сразу и ни одной дорожки). Общего типа у
// них нет намеренно — «нет аудио» это свойство формата, а не настройка, — но
// правила ниже нужны обоим одинаково, и копия любого из них означала бы, что
// одна и та же песня на двух поверхностях описана по-разному. Одна такая копия
// уже стоила бага: расписание слежения было отфильтровано по именам у аудио-
// шейра и не отфильтровано у текстовой ссылки (B-260829).
//
// Обе функции чистые и ничего не читают из окружения: ни `document`, ни
// состояния. Поэтому они проверяемы в happy-dom, который раскладку не считает
// вовсе.

import type { SectionSec } from './chart-follow.js';

/**
 * ПОЛНАЯ сетка аранжировки: все секции подряд, с именами и без.
 *
 * Расписание для СЛЕЖЕНИЯ, одно на обе поверхности. Замер живого стенда
 * (опубликованная ссылка Сергея, песня «Во Христе я всё смогу»):
 *
 *   'Intro'    3.69 → 25.85
 *   'Verse 1' 25.85 → 40.62
 *   ''        40.62 → 49.85   ← без имени
 *   'CHORUS'  49.85 → 68.31
 *   ''        68.31 → 73.85   ← без имени
 *
 * Отфильтрованный по именам список оставляет в расписании ДЫРУ 40.62 → 49.85,
 * а `sectionIndexAt` внутри дыры отвечает −1 — и ведение выходило до пересчёта
 * блока, оставляя подсветку на прошлом. Девять секунд лист уверенно показывал
 * Verse 1, пока играл безымянный проигрыш, и рывком прыгал на CHORUS. Сергей
 * назвал это «отстаёт на секунду-две»; замер говорит про девять.
 *
 * Корень — одна коллекция на две задачи. Позиция принадлежит ВСЕЙ аранжировке,
 * а имена нужны только для сопоставления секции с блоком чарта. Поэтому фильтра
 * по имени здесь нет, а есть проверка чисел: секция без годных границ — не
 * участник расписания, её не выбрала бы ни одна позиция.
 *
 * ⚠️ Отфильтрованный по именам близнец (`namedSections`) жил здесь же и питал
 * аудио-шейр до 31.08 — дефект B-260829, одна и та же ошибка на двух
 * поверхностях, починенная по одной. Больше его нет: пока рядом лежат два
 * похожих списка, следующая поверхность возьмёт не тот. Правило «имя решает
 * ровно одно — найдётся ли блок» стоит там, где сопоставляют (`buildMap` в
 * chart-follow.ts), и в форме расписания ему делать нечего.
 *
 * Вход — `unknown`: и манифест, и нагрузка приходят из сети, где «поля нет»,
 * `null` и мусор одинаково возможны.
 */
export function gridSections(src: unknown): SectionSec[] {
  if (!Array.isArray(src)) return [];
  const out: SectionSec[] = [];
  for (const raw of src) {
    if (raw == null || typeof raw !== 'object') continue;
    const s = raw as { id?: unknown; name?: unknown; startSec?: unknown; endSec?: unknown };
    const start = s.startSec;
    const end = s.endSec;
    if (typeof start !== 'number' || !Number.isFinite(start)) continue;
    if (typeof end !== 'number' || !Number.isFinite(end)) continue;
    // Пустая и вывернутая секция не «почти годится»: `sectionIndexAt` требует
    // `start <= pos < end` и такую не выберет никогда.
    if (end <= start) continue;
    out.push({
      id: typeof s.id === 'string' ? s.id : '',
      // Имени нет — пустая строка, а НЕ выброс: проигрыш, обрыв и
      // инструментальный хвост это законные записи аранжировки, и их время
      // принадлежит песне.
      name: typeof s.name === 'string' ? s.name : '',
      startSec: start,
      endSec: end,
    });
  }
  // ПОРЯДОК ПО ВРЕМЕНИ — здесь, а не у потребителей (03.09). На проводе он не
  // гарантирован ничем: `addSection` на десктопе дописывает новую секцию в
  // КОНЕЦ массива, `assertValidSections` порядка не требует, а
  // `sectionsToSeconds` отдаёт как лежит — на диске уже есть песня, где куплет
  // из девятого такта стоит последним, после аутро.
  //
  // Веб от порядка ЗАВИСИТ: `buildCache` раздаёт копии одного напечатанного
  // блока последовательно, сверху вниз, поэтому припев, сыгранный дважды, при
  // перевёрнутом хранении достался бы копиям задом наперёд — и лист уезжал бы
  // НАЗАД. Десктоп ту же защиту делает `orderByTime` из ядра.
  //
  // Сортировка устойчивая (`sort` в JS стабилен с ES2019): секции с одинаковым
  // началом сохраняют порядок хранения, и это не косметика — своевольная
  // перестановка равных дала бы двум поверхностям разный ответ на одинаковых
  // данных, то есть ровно то расхождение, от которого эта строка защищает.
  out.sort((a, b) => a.startSec - b.startSec);
  return out;
}

/**
 * Строка меты песни: «C · 72 bpm · 4/4».
 *
 * Части собираются по одной, а не шаблоном: и манифест, и нагрузка приходят из
 * сети, и шаблонная склейка печатала бы на стойке «undefined · undefined bpm ·
 * undefined». Нечем заполнить — пустая строка, и поверхность тогда не рисует
 * мету вовсе (пустая строка в шапке — дыра, а не сведения).
 *
 * Тип аргумента структурный, а не `ShareManifest['song']`: у нагрузки текстовой
 * ссылки песня той же формы в этих трёх полях и совсем другой во всём
 * остальном. Приводить её к манифесту значило бы соврать о наличии дорожек.
 */
export interface SongMetaLike {
  key?: unknown;
  bpm?: unknown;
  timeSignature?: unknown;
}

export function songMetaLine(song: SongMetaLike | null | undefined): string {
  const parts: string[] = [];
  const key = song?.key;
  const bpm = song?.bpm;
  const sig = song?.timeSignature;
  if (typeof key === 'string' && key.trim() !== '') parts.push(key.trim());
  // Ноль, отрицательный и NaN — это «темп неизвестен», а не «0 bpm».
  if (typeof bpm === 'number' && Number.isFinite(bpm) && bpm > 0) parts.push(`${bpm} bpm`);
  if (typeof sig === 'string' && sig.trim() !== '') parts.push(sig.trim());
  return parts.join(' · ');
}
